import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, catchError, map } from 'rxjs';
import { GetUserEmailDto, ResetPasswordDto } from 'src/app/admin/models/account-model';
import { AppConsts } from 'src/app/shared/app-consts';
import { BaseComponent } from 'src/app/shared/base/base-component';
import { AuthService } from 'src/app/shared/providers/auth/auth-service.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent extends BaseComponent implements OnInit, OnDestroy {
  id: string;
  email: string;

  errorMessage: string;
  showPassword: boolean = false;
  isInvalid: boolean = false;
  invalidMessage: string = '';
  isPasswordChanged: boolean = false;

  private _error = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    super(true);
  }

  ngOnInit(): void {
    const hash = window.location.hash?.substring(1) ?? '';
    const identifier = localStorage.getItem(AppConsts.Session_ForceChangePassword) ?? '';
    this.id = hash ? hash : identifier;

    if (this.id) {
      this.getEmail();
    }
    else {
      this.translate.get('ResetPassword.InvalidLink').subscribe(msg => {
        this.isInvalid = true;
        this.invalidMessage = msg;
      });
    }
    this._error.subscribe((message) => (this.errorMessage = message));

    this.createForm();
  }

  ngOnDestroy(): void {
    this.subscription.forEach((sb) => sb.unsubscribe());
  }

  save() {
    this.submitted = true;
    this._error.next('');
    if (this.formGroup.valid) {
      const data = this.form_Group.controls;
      const obj = {
        identifier: data['identifier'].value,
        email: data['email'].value,
        password: data['password'].value,
        confirmPassword: data['confirmPassword'].value
      } as ResetPasswordDto;

      const sub = this.authService.resetPassword(obj)
        .subscribe(() => {
          const userToken = localStorage.getItem(AppConsts.Session_AccessToken)
          if (userToken) {
            localStorage.clear();
            this.navigateToURL("/adminlogin");
          }

          this.isPasswordChanged = true;
        }, error => {
          this.submitted = false;
          if (error.status === 403)
            this.translate.get(`ForgotPassword.${error.error.error.message}`).subscribe(msg => this._error.next(msg));
          else
            this.translate.get('ErrorCodes.GeneralError').subscribe(msg => this._error.next(msg));

          return this.logException(error);
        });

      this.subscription.push(sub);
    }
  }
  confirmPasswordValidator() {
    const password: any | undefined = this.formGroup.get("password");
    const confirmPassword: any | undefined = this.formGroup.get("confirmPassword");

    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMatch: true });
    } else {
      confirmPassword.setErrors(null);
    }

    confirmPassword.updateValueAndValidity();
  }
  private getEmail() {
    const sub = this.authService.getUserEmail(this.id)
      .pipe(
        map((response: GetUserEmailDto) => {
          if (response) {
            this.email = response.email;
            this.isInvalid = false;
            this.invalidMessage = '';

            this.createForm();
          }
        }),
        catchError((err) => {
          if (err.status === 403 && err.error.error.message === 'ExpiredLink') {
            this.translate.get('EmailActivation.ExpiredLink').subscribe(msg => {
              this.invalidMessage = msg;
            });
          }
          else {
            this.translate.get('ResetPassword.InvalidLink').subscribe(msg => {
              this.invalidMessage = msg;
            });
            this.isInvalid = true;
            return this.logException(err);
          }
        })
      )
      .subscribe();

    this.subscription.push(sub);
  }
  private createForm() {
    this.formGroup = this.fb.group({
      identifier: [this.id],
      email: [this.email],
      password: [''],
      confirmPassword: [''],
    }, { validator: this.passwordMatchValidator('password', 'confirmPassword') });

    this.formGroup.statusChanges.subscribe(() => {/* console.log("test ", result)*/ });

    this.setCustomValidators();
  }
  private setCustomValidators() {
    const identifier: any | undefined = this.formGroup.get("identifier");
    const email: any | undefined = this.formGroup.get("email");
    const password: any | undefined = this.formGroup.get("password");
    const confirmPassword: any | undefined = this.formGroup.get("confirmPassword");
    let passwordRegex = `^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?!.*${this.emailRegexFormat(this.email)}).{8,}$`;

    identifier.setValidators([
      Validators.required,
    ]);
    email.setValidators([
      Validators.required,
    ]);
    password.setValidators([
      Validators.required,
      Validators.pattern(passwordRegex),
    ]);
    confirmPassword.setValidators([
      Validators.required,
    ]);



    identifier.updateValueAndValidity();
    email.updateValueAndValidity();
    password.updateValueAndValidity();
    confirmPassword.updateValueAndValidity();
  }
  private resetForm() {
    this.submitted = false;
    this.createForm();
  }
  private emailRegexFormat(email: string): string {
    // Split the email into username and domain parts
    const [username, domain] = email.split('@');

    // Convert each character in the username to the desired format
    const modifiedUsername = username
      .split('')
      .map((char) => `[${char.toLowerCase()}${char.toUpperCase()}]`)
      .join('');

    return modifiedUsername;
  }
  private passwordMatchValidator(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const passwordControl = formGroup.get(controlName);
      const confirmPasswordControl = formGroup.get(matchingControlName);
      if (passwordControl && confirmPasswordControl) {
        if (passwordControl.value !== confirmPasswordControl.value) {
          confirmPasswordControl.setErrors({ passwordMatch: true });
        } else {
          confirmPasswordControl.setErrors(null);
        }
      }
    };
  }
}
